import type { StorageKey } from "@/shared/lib/storage.constants";

// Thin facade over Web Storage: one SSR guard + one try/catch instead of
// repeating both at every call site, plus typed keys via STORAGE_KEYS.
//
// Deliberately thin: get/set/remove + JSON variants only. NO reactivity,
// cross-tab sync, TTL, or expiry — add those only if an actual need arises.
// All access is best-effort: if storage fails (private mode, sandboxed
// iframe, quota), the call fails silently instead of throwing.

type StorageKind = "local" | "session";

function resolveStore(kind: StorageKind): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return kind === "local" ? window.localStorage : window.sessionStorage;
  } catch {
    // Accessing the storage object itself can throw in sandboxed iframes /
    // strict private mode. Treat as unavailable.
    return null;
  }
}

function createStorage(kind: StorageKind) {
  function get(key: StorageKey): string | null {
    const store = resolveStore(kind);
    if (!store) return null;
    try {
      return store.getItem(key);
    } catch {
      // Reads can still throw after resolve — treat as absent.
      return null;
    }
  }

  function set(key: StorageKey, value: string): void {
    const store = resolveStore(kind);
    if (!store) return;
    try {
      store.setItem(key, value);
    } catch {
      // Quota exceeded or private mode — drop the write silently.
    }
  }

  function remove(key: StorageKey): void {
    const store = resolveStore(kind);
    if (!store) return;
    try {
      store.removeItem(key);
    } catch {
      // Ignore — the key is on its way out anyway.
    }
  }

  function getJSON<T>(key: StorageKey): T | null {
    const raw = get(key);
    if (raw === null) return null;
    try {
      return JSON.parse(raw) as T;
    } catch {
      // Corrupt state — treat as absent rather than throwing at the caller.
      return null;
    }
  }

  function setJSON(key: StorageKey, value: unknown): void {
    try {
      set(key, JSON.stringify(value));
    } catch {
      // Circular reference or similar — skip the write.
    }
  }

  return { get, set, remove, getJSON, setJSON };
}

export const local = createStorage("local");
export const session = createStorage("session");
